import { http } from '@/utils/http'
import type { IApiResponse, IAuthResponse } from '@/types'
import { getConfig } from '@/config'
import { useAuthStore } from '@/stores/auth'
import { AxiosRequestConfig } from 'axios'
import axios from 'axios'
import { logger, createLogger } from '@/utils/logger'
import type { DeviceService } from './device.service'

// Create a namespaced logger
const fileLogger = createLogger('ApiService')

export enum ApiRoutes {
  Login = '/login',
  Register = '/register',
  ForgotPassword = '/password-forgot',
  ValidateResetCode = '/password-reset-validate',
  ResetPassword = '/password-reset',
  Me = '/me',
  UpdatePassword = '/me/password',
  Deactivate = '/me/deactivate',
  DeactivationStatus = '/me/deactivation-status',
  Reactivate = '/me/reactivate',
  Sync = '/sync',
  SupportContact = '/contact'
}

/**
 * Service for making API requests
 */
export class ApiService {
  private baseUrl: string
  private version: string
  private commonHeaders: Record<string, string>
  private deviceService: DeviceService | null = null

  constructor() {
    const config = getConfig()
    this.baseUrl = (config.api.baseUrl || '').replace(/\/$/, '')
    this.version = config.api.version
    this.commonHeaders = this.getCommonHeaders()
  }

  /**
   * Set device service for device ID management
   */
  setDeviceService(deviceService: DeviceService): void {
    this.deviceService = deviceService
  }

  private buildUrl(path: string, isAuthRoute: boolean = false, skipGuestPrefix: boolean = false): string {
    if (isAuthRoute) {
      return `${this.baseUrl}/${this.version}${path}`
    }
    
    const authStore = useAuthStore()
    const config = getConfig()
    const isGuest = !authStore.isAuthenticated && config.features.guestMode && !skipGuestPrefix
    const prefix = isGuest ? 'guest/' : ''
    const cleanPath = path.replace(/^\//, '')
    return `${this.baseUrl}/${this.version}/${prefix}${cleanPath}`
  }

  /**
   * Get common headers for API requests
   * @returns Headers object with common headers
   */
  private getCommonHeaders(): Record<string, string> {
    const config = getConfig()
    return {
      'Content-Type': 'application/vnd.api+json',
      'Accept': 'application/vnd.api+json',
      'X-App-ID': config.app.contentId.toString()
    }
  }

  /**
   * Get headers with device ID included
   * @returns Headers object with device ID
   */
  private async getHeadersWithDeviceId(): Promise<Record<string, string>> {
    const headers = { ...this.commonHeaders }
    
    if (this.deviceService) {
      try {
        const deviceId = await this.deviceService.getDeviceId()
        if (deviceId) {
          headers['X-Device-ID'] = deviceId
        }
      } catch (error) {
        fileLogger.warn('Failed to get device ID for request:', error)
      }
    }
    
    return headers
  }

  /**
   * Check if a path is an authentication route
   * @param path - The path to check
   * @returns True if the path is an authentication route
   */
  private isAuthRoute(path: string): boolean {
    return Object.values(ApiRoutes).includes(path as ApiRoutes)
  }

  /**
   * Make a GET request to the API
   * @param path - The path to make the request to
   * @param params - Optional parameters for the request
   * @returns A promise that resolves to the response data
   */
  async get<T = any>(path: string, params?: Record<string, any>): Promise<T> {
    try {
      // fileLogger.log(`Making GET request to: ${path}`, { params })
      
      const isAuthRoute = this.isAuthRoute(path)
      const url = this.buildUrl(path, isAuthRoute)
      const headers = await this.getHeadersWithDeviceId()
      
      const response = await http.get<T>(url, { 
        params,
        headers,
        withCredentials: false
      })
      
      // fileLogger.log(`GET request successful: ${path}`)
      return response
    } catch (error) {
      fileLogger.error(`GET request failed: ${path}`, error)
      throw error
    }
  }

  /**
   * Make a POST request to the API
   * @param path - The path to make the request to
   * @param data - The data to send in the request body
   * @param skipGuestPrefix - If true, skip the guest/ prefix even for unauthenticated users
   * @returns A promise that resolves to the response data
   */
  async post<T = any>(path: string, data?: any, skipGuestPrefix: boolean = false): Promise<T> {
    try {
      fileLogger.log(`Making POST request to: ${path}`, { data, skipGuestPrefix })
      
      const isAuthRoute = this.isAuthRoute(path)
      const url = this.buildUrl(path, isAuthRoute, skipGuestPrefix)
      const headers = await this.getHeadersWithDeviceId()
      
      const response = await http.post<T>(url, data, {
        headers,
        withCredentials: false
      })
      
      fileLogger.log(`POST request successful: ${path}`)
      return response
    } catch (error) {
      fileLogger.error(`POST request failed: ${path}`, error)
      throw error
    }
  }

  /**
   * Make a PUT request to the API
   * @param path - The path to make the request to
   * @param data - The data to send in the request body
   * @returns A promise that resolves to the response data
   */
  async put<T = any>(path: string, data: any): Promise<T> {
    try {
      fileLogger.log(`Making PUT request to: ${path}`, { data })
      
      const isAuthRoute = this.isAuthRoute(path)
      const url = this.buildUrl(path, isAuthRoute)
      const headers = await this.getHeadersWithDeviceId()
      
      const response = await http.put<T>(url, data, {
        headers,
        withCredentials: false
      })
      
      fileLogger.log(`PUT request successful: ${path}`)
      return response
    } catch (error) {
      fileLogger.error(`PUT request failed: ${path}`, error)
      throw error
    }
  }

  /**
   * Make a PATCH request to the API
   * @param path - The path to make the request to
   * @param data - The data to send in the request body
   * @returns A promise that resolves to the response data
   */
  async patch<T = any>(path: string, data: any): Promise<T> {
    try {
      fileLogger.log(`Making PATCH request to: ${path}`, { data })
      
      const isAuthRoute = this.isAuthRoute(path)
      const url = this.buildUrl(path, isAuthRoute)
      const headers = await this.getHeadersWithDeviceId()
      
      const response = await http.patch<T>(url, data, {
        headers,
        withCredentials: false
      })
      
      fileLogger.log(`PATCH request successful: ${path}`)
      return response
    } catch (error) {
      fileLogger.error(`PATCH request failed: ${path}`, error)
      throw error
    }
  }

  /**
   * Make a POST request to the API for authentication
   * @param path - The path to make the request to
   * @param data - The data to send in the request body
   * @returns A promise that resolves to the response data
   */
  async auth(path: string, data?: any): Promise<IAuthResponse> {
    try {
      fileLogger.log(`Making auth request to: ${path}`, { data })
      
      const url = this.buildUrl(path, true)
      const headers = await this.getHeadersWithDeviceId()
      const response = await http.post<IAuthResponse>(url, data, {
        headers,
        withCredentials: false
      })
      
      fileLogger.log(`Auth request successful: ${path}`)
      return response
    } catch (error) {
      fileLogger.error(`Auth request failed: ${path}`, error)
      throw error
    }
  }

  /**
   * Initialize app purchase - public endpoint (no auth required)
   * This endpoint returns plain JSON (not JSON API format), so we use axios directly
   * to bypass the JSON API transformer in the http utility
   * @param tuuid - Title UUID
   * @param uhash - User hash (MD5 of user ID)
   * @returns User details including stripe_id (may be null) and title_id
   */
  async initAppPurchase(tuuid: string, uhash: string): Promise<{
    id: number
    email: string
    name: string
    stripe_id: string | null
    app_id: number
    title_id: number
  }> {
    try {
      fileLogger.log('Initializing app purchase', { tuuid, uhash })
      
      // This is a public endpoint, so build URL without auth prefix
      const url = `${this.baseUrl}/${this.version}/app-purchase/init`
      
      // Use axios directly to bypass the JSON API transformer
      // This endpoint returns plain JSON: { data: { id, email, name, ... } }
      const response = await axios.post<{
        data: {
          id: number
          email: string
          name: string
          stripe_id: string | null
          app_id: number
          title_id: number
        }
      }>(url, {
        tuuid,
        uhash
      }, {
        headers: this.commonHeaders,
        withCredentials: false
      })
      
      fileLogger.log('App purchase init raw response:', response.data)
      
      // Extract the data field from the response
      const result = response.data.data
      
      fileLogger.log('App purchase init successful, extracted data:', result)
      
      // Validate that we have the required fields
      if (!result || !result.id || result.title_id === undefined) {
        fileLogger.error('Invalid response structure:', result)
        throw new Error('Invalid response from purchase initialization endpoint')
      }
      
      return result
    } catch (error) {
      fileLogger.error('App purchase init failed', error)
      throw error
    }
  }
} 